import { z } from 'zod';
import { EmailProviderName, NotificationStatus } from '../enums';
import { DELIVERY_STATES } from '../email/delivery';
import { secretInput } from './settings';

const emailOrBlank = z
  .string()
  .trim()
  .toLowerCase()
  .max(254)
  .refine((v) => v === '' || z.email().safeParse(v).success, 'must be a valid email address or blank');

export const NotificationSettings = z.object({
  enabled: z.boolean(),
  admissionsInbox: emailOrBlank,
  fromName: z
    .string()
    .trim()
    .min(1)
    .max(100)
    .regex(/^[^\p{Cc}<>"]*$/u, 'must not contain control characters, quotes or angle brackets'),
  replyTo: emailOrBlank,
});
export type NotificationSettings = z.infer<typeof NotificationSettings>;

export const NotificationSettingsView = NotificationSettings.extend({
  smtpPasswordSet: z.boolean(),
  delivery: z.object({
    provider: EmailProviderName,
    configured: z.boolean(),
    fromAddress: z.string(),
    smtpHost: z.string().nullable(),
    smtpPort: z.number().int(),
    smtpUsername: z.string().nullable(),
    gmailServiceAccount: z.string().nullable(),
  }),
});
export type NotificationSettingsView = z.infer<typeof NotificationSettingsView>;

export const UpdateNotificationSettings = NotificationSettings.partial().extend({ smtpPassword: secretInput });
export type UpdateNotificationSettings = z.infer<typeof UpdateNotificationSettings>;

export const NotificationDto = z.object({
  id: z.string().uuid(),
  kind: z.string(),
  kindLabel: z.string(),
  recipientEmail: z.string(),
  subject: z.string(),
  status: NotificationStatus,
  attempts: z.number().int(),
  lastError: z.string().nullable(),
  createdAt: z.string(),
  sentAt: z.string().nullable(),
  state: z.enum(DELIVERY_STATES),
  resendOfId: z.string().uuid().nullable(),
  isResend: z.boolean(),
});
export type NotificationDto = z.infer<typeof NotificationDto>;

export const DeliveryStateFilter = z.enum(['all', ...DELIVERY_STATES]);
export type DeliveryStateFilter = z.infer<typeof DeliveryStateFilter>;

const emptyToUndefined = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? undefined : v);

export const NotificationListQuery = z
  .object({
    kind: z.preprocess(emptyToUndefined, z.string().trim().max(100).optional()),
    recipient: z.preprocess(emptyToUndefined, z.string().trim().max(200).optional()),
    from: z.preprocess(emptyToUndefined, z.iso.date().optional()),
    to: z.preprocess(emptyToUndefined, z.iso.date().optional()),
    state: DeliveryStateFilter.default('all'),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
  })
  .refine((q) => !q.from || !q.to || q.from <= q.to, { path: ['to'], message: 'The end date must be on or after the start date' });
export type NotificationListQuery = z.infer<typeof NotificationListQuery>;

export const TestEmailResult = z.object({
  notificationId: z.string().uuid(),
  recipientEmail: z.string(),
});
export type TestEmailResult = z.infer<typeof TestEmailResult>;

export const EmailPreviewDto = z.object({
  kind: z.string(),
  label: z.string(),
  audience: z.string(),
  subject: z.string(),
  text: z.string(),
  html: z.string(),
});
export type EmailPreviewDto = z.infer<typeof EmailPreviewDto>;

export const EmailPreviewResponse = z.object({
  from: z.string(),
  replyTo: z.string().nullable(),
  templates: z.array(EmailPreviewDto),
});
export type EmailPreviewResponse = z.infer<typeof EmailPreviewResponse>;

export const DeliveryCounts = z.object({
  all: z.number().int(),
  delivered: z.number().int(),
  failed: z.number().int(),
  pending: z.number().int(),
});
export type DeliveryCounts = z.infer<typeof DeliveryCounts>;

export const NotificationListResponse = z.object({
  items: z.array(NotificationDto),
  counts: DeliveryCounts,
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
});
export type NotificationListResponse = z.infer<typeof NotificationListResponse>;

const Severity = z.enum(['ok', 'warning', 'critical']);

export const DeliveryHealthAlert = z.object({
  code: z.enum(['failure_rate', 'delayed', 'kill_switch_off']),
  severity: z.enum(['warning', 'critical']),
  message: z.string(),
});
export type DeliveryHealthAlert = z.infer<typeof DeliveryHealthAlert>;

export const DeliveryHealth = z.object({
  windowHours: z.number().int(),
  total: z.number().int(),
  delivered: z.number().int(),
  failed: z.number().int(),
  pending: z.number().int(),
  failureRatePercent: z.number(),
  delayed: z.number().int(),
  oldestPendingMinutes: z.number().int().nullable(),
  sendingEnabled: z.boolean(),
  severity: Severity,
  alerts: z.array(DeliveryHealthAlert),
});
export type DeliveryHealth = z.infer<typeof DeliveryHealth>;

export const PurgePendingInput = z.object({
  requestId: z.string().uuid(),
  confirmedCount: z.number().int().min(0),
});
export type PurgePendingInput = z.infer<typeof PurgePendingInput>;

export const PurgePendingResult = z.object({
  requestId: z.string().uuid(),
  confirmedCount: z.number().int(),
  attempted: z.number().int(),
  deleted: z.number().int(),
  replayed: z.boolean(),
});
export type PurgePendingResult = z.infer<typeof PurgePendingResult>;
